import EcomTotalSellsChart from 'components/charts/e-charts/EcomTotalSellsChart';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import { useQuery } from '@apollo/client';
import { ORDERS_COMPARISON_QUERY } from 'api/graphql/queries/ordersComparisonQuery';
import dayjs from 'dayjs';

interface DailyCount {
  date: string;
  count: number;
  amount: number;
}

interface EcomTotalSellsProps {
  className?: string;
}

const EcomTotalSells: React.FC<EcomTotalSellsProps> = ({ className }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<number>(1);
  // Fetch data using the new comparison API
  const { data, loading, error } = useQuery(ORDERS_COMPARISON_QUERY, {
    variables: { period },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first'
  });

  // Extract data from query result
  const comparisonData = data?.getOrdersComparisonData;
  let currentPeriod = comparisonData?.currentPeriod || [];
  let previousPeriod = comparisonData?.previousPeriod || [];
  const periodLabel = comparisonData?.periodLabel || '';
  
  // Filter out future dates and same date from previous month
  const today = dayjs().format('YYYY-MM-DD');
  
  // Filter out future dates from current period
  currentPeriod = currentPeriod.filter((item: DailyCount) => item.date <= today);
  
  // Filter out the same date from previous month
  const currentDates = currentPeriod.map((item: DailyCount) => dayjs(item.date).date());
  previousPeriod = previousPeriod.filter((item: DailyCount) => {
    const itemDate = dayjs(item.date);
    const dayOfMonth = itemDate.date();
    return currentDates.includes(dayOfMonth);
  });
  
  // Recalculate totals after filtering
  const totalCurrentPeriod = currentPeriod.reduce((sum: number, day: DailyCount) => sum + (day.count || 0), 0);
  const totalPreviousPeriod = previousPeriod.reduce((sum: number, day: DailyCount) => sum + (day.count || 0), 0);
  const percentageChange = totalPreviousPeriod ? ((totalCurrentPeriod - totalPreviousPeriod) / totalPreviousPeriod) * 100 : 0;

  // Calculate total amounts
  const totalCurrentAmount = currentPeriod.reduce((sum: number, day: DailyCount) => sum + (day.amount || 0), 0);
  const totalPreviousAmount = previousPeriod.reduce((sum: number, day: DailyCount) => sum + (day.amount || 0), 0);
  const amountPercentageChange = totalPreviousAmount ? ((totalCurrentAmount - totalPreviousAmount) / totalPreviousAmount) * 100 : 0;

  // Format percentages for display
  const formattedPercentage = `${percentageChange >= 0 ? '+' : ''}${percentageChange.toFixed(1)}%`;
  const formattedAmountPercentage = `${amountPercentageChange >= 0 ? '+' : ''}${amountPercentageChange.toFixed(1)}%`;
  const isPositiveChange = percentageChange >= 0;
  const isPositiveAmountChange = amountPercentageChange >= 0;

  // Handle period change
  const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPeriod(parseInt(e.target.value, 10));
  };

  return (
    <>
      <Row className="justify-content-between align-items-center mb-4 g-3">
        <Col>
          <h3>
            {t('salesSummary.title')}
          </h3>
          <Row className="mt-3">
              <div className="d-flex align-items-center mb-2">
                <h5 className="me-2 mb-0">{t('salesSummary.orders')}</h5>
                <h5 className="mb-0">{totalCurrentPeriod}</h5>
                <Badge
                  bg={isPositiveChange ? "success" : "danger"}
                  variant="phoenix"
                  pill
                  className="ms-2"
                  style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}
                >
                  {formattedPercentage}
                </Badge>
              </div>
              {/* <p className="text-body-tertiary lh-sm mb-0">
                {totalCurrentPeriod} orders vs {totalPreviousPeriod} in previous period
              </p> */}
              <div className="d-flex align-items-center mb-2">
                <h5 className="me-2 mb-0">{t('salesSummary.sales')}</h5>
                <h5 className="mb-0">{totalCurrentAmount.toFixed(2)} {t('transactionSummary.dinarSymbol')}</h5>
                <Badge
                  bg={isPositiveAmountChange ? "success" : "danger"}
                  variant="phoenix"
                  pill
                  className="ms-2"
                  style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}
                >
                  {formattedAmountPercentage}
                </Badge>
              </div>
              {/* <p className="text-body-tertiary lh-sm mb-0">
                {totalCurrentAmount.toFixed(2)} DT vs {totalPreviousAmount.toFixed(2)} DT in previous period
              </p> */}
          </Row>
        </Col>
        <Col xs="auto">
          <Form.Select
            size="sm"
            value={period}
            onChange={handlePeriodChange}
            className="mt-2"
          >
            <option value={1}>{t('salesSummary.last30days')}</option>
            <option value={2}>{t('salesSummary.last60days')}</option>
            <option value={3}>{t('salesSummary.last90days')}</option>
          </Form.Select>
        </Col>
      </Row>
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '316px' }}>
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">{t('salesSummary.loading')}</span>
          </Spinner>
        </div>
      ) : (
        <EcomTotalSellsChart
          currentMonthData={currentPeriod}
          prevMonthData={previousPeriod}
          periodLabel={periodLabel}
          isPeriodMonth={period === 1}
          period={period}
          showBoth={true} // Show both count and amount data
        />
      )}
    </>
  );
};

export default EcomTotalSells;
